import React, { useCallback, useRef, useEffect } from "react";
import { Label } from "../../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import { AutomatedControlData } from "../types-automated";

interface RiskAssociatedAutomatedProps {
  data: AutomatedControlData;
  onChange: (data: Partial<AutomatedControlData>) => void;
}

export const RiskAssociatedAutomated: React.FC<RiskAssociatedAutomatedProps> = ({ data, onChange }) => {
  // Use ref to access latest onChange without causing callback recreation
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const riskFactors = [
    "The nature and materiality of misstatements that the control is intended to prevent or detect,",
    "Whether there have been changes in the volume or nature of transactions that might adversely affect control design or operating effectiveness,",
    "Whether the account balance, class of transaction or disclosure has a history of errors,",
    "The effectiveness of entity-level controls, especially controls that monitor other controls,",
    "The nature of the control and the frequency with which it operates,",
    "The degree to which the control relies on the effectiveness of other controls",
    "The competence of the personnel who perform the control or monitor its performance and whether there have been any changes in authority and responsibility,",
    "Whether the control relies on performance by an individual or is automated (an automated control would generally have lower risk if relevant ITGC controls are effective)",
    "The complexity of the control and the significance of the judgements that must be made in connection with its operation.",
  ];

  const handleRiskAssociatedChange = useCallback((value: string) => {
    onChangeRef.current({ riskAssociated: value as "Higher" | "Not Higher" });
  }, []);

  // Show "Not Applicable" if testOperatingEffectiveness is false
  if (!data.testOperatingEffectiveness) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg">
        <div className="border-b border-white/10 p-6">
          <h2 className="text-xl font-semibold text-white">Risk Associated with Control</h2>
        </div>
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <p className="text-lg text-white/60">Not Applicable</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Risk Associated with Control</h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Risk Factors List */}
        <div className="space-y-4">
          <Label className="text-white">Factors that affect the risk associated with a control include:</Label>
          <ul className="space-y-2 pl-4">
            {riskFactors.map((factor, index) => (
              <li
                key={index}
                className="text-sm text-white/60 list-disc list-inside"
              >
                {factor}
              </li>
            ))}
          </ul>
        </div>

        {/* Risk Assessment */}
        <div className="flex items-center gap-4 border-t border-white/10 pt-6">
          <Label className="flex-1 text-white">What is the risk associated with the control?</Label>
          <Select
            value={data.riskAssociated || "Not Higher"}
            onValueChange={handleRiskAssociatedChange}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-black/90 border-white/10">
              <SelectItem value="Higher">Higher</SelectItem>
              <SelectItem value="Not Higher">Not Higher</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Basis for Conclusion */}
        <div className="space-y-4">
          <Label className="text-white">Document the basis for the conclusion on the Risk associated with the control:</Label>
          <RichTextEditor
            value={data.riskBasisConclusion}
            onChange={(content) => onChangeRef.current({ riskBasisConclusion: content })}
            placeholder="Enter basis for conclusion..."
          />
        </div>
      </div>
    </div>
  );
};

